import { getAntiForgeryToken } from '../../lib/helpers';
import { renderAiUsageBadge, readLabelsFromContainer, AiUsage } from '../ai/ai-usage-badge';
import { getAgencyId } from './contract-dom';

interface FormFill {
    field: string;
    value: string;
    label?: string;
    isSuggestion: boolean;
}

interface UnitDateRange {
    unitId: number;
    from: string;
    to: string;
    isSuggestion: boolean;
}

interface AssistantResult {
    sessionId: number;
    assistantMessage: string;
    formFills: FormFill[];
    unitDateRanges?: UnitDateRange[];
}

interface AgencyCaps {
    contractAssistant: boolean;
    contractAssistantMode: string;
}

type AssistantMode = 'Verify' | 'Highlight' | 'AutoFill';

const DATE_FIELDS = new Set(['CheckInDate', 'CheckOutDate', 'PaymentDueDate']);

export function initContractAssistant(form: HTMLFormElement) {
    const sendBtn    = document.getElementById('assistant-send');
    const input      = document.getElementById('assistant-input') as HTMLInputElement | null;
    const messagesEl = document.getElementById('assistant-messages');
    const chatCard   = document.getElementById('assistant-chat-card');

    // Parse per-agency caps once
    let capsByAgency: Record<string, AgencyCaps> = {};
    try {
        capsByAgency = JSON.parse(form.dataset.aiCapsByAgency || '{}');
    } catch {
        // malformed — ignore, fall back to static data-attributes
    }

    if (!sendBtn || !input || !messagesEl) return;

    // Capture as non-null locals so closures below don't need ! assertions.
    const btn      = sendBtn;
    const inputEl  = input;
    const msgsEl   = messagesEl;

    let sessionId: number | null = null;

    const getCaps = (): { url: string; mode: AssistantMode } => {
        const agencyId = getAgencyId(form);
        const perAgency = capsByAgency[agencyId];
        if (perAgency) {
            return {
                url: perAgency.contractAssistant ? (form.dataset.urlAssistantChat || '') : '',
                mode: (perAgency.contractAssistantMode as AssistantMode) || 'Verify',
            };
        }
        return {
            url: form.dataset.urlAssistantChat || '',
            mode: (form.dataset.aiAssistantMode as AssistantMode) || 'Verify',
        };
    };

    // When admin switches agency: show/hide chat card based on the new agency's caps
    const agencyPicker = document.getElementById('AgencyId') as HTMLSelectElement | null;
    if (agencyPicker && chatCard) {
        agencyPicker.addEventListener('change', () => {
            const { url } = getCaps();
            chatCard.style.display = url ? '' : 'none';
        });
    }

    const labels = readLabelsFromContainer(form);

    btn.addEventListener('click', () => sendMessage());
    inputEl.addEventListener('keydown', e => { if (e.key === 'Enter') sendMessage(); });

    async function sendMessage() {
        const msg = inputEl.value.trim();
        if (!msg) return;

        const { url: urlAssistantChat, mode } = getCaps();
        if (!urlAssistantChat) {
            appendMessage('assistant', 'AI assistant is not available for the selected agency.');
            return;
        }

        appendMessage('user', msg);
        inputEl.value = '';
        btn.setAttribute('disabled', '');

        try {
            const token    = getAntiForgeryToken();
            const agencyId = getAgencyId(form);
            const resp = await fetch(`${urlAssistantChat}?agencyId=${agencyId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'RequestVerificationToken': token },
                body: JSON.stringify({ sessionId, message: msg })
            });
            const json = await resp.json();

            if (!json.success) {
                appendMessage('assistant', json.message ?? 'Error');
                return;
            }

            const data: AssistantResult = json.data;
            sessionId = data.sessionId;

            let bubble: HTMLElement | null = null;
            if (data.assistantMessage) {
                bubble = appendMessage('assistant', data.assistantMessage);
            }

            if (json.usage && bubble) {
                bubble.insertAdjacentHTML('afterend', renderAiUsageBadge(json.usage as AiUsage, labels));
            }

            if (data.formFills?.length) {
                applyFormFills(data.formFills, mode);
            }

            if (data.unitDateRanges?.length) {
                applyUnitDateRanges(data.unitDateRanges, mode);
            }

            // Bridge: if AI proposed CheckIn/CheckOut via legacy fields but no unitDateRanges,
            // propagate those dates into the first unit card of the selected accommodation offer.
            if (data.formFills?.length && !data.unitDateRanges?.length) {
                bridgeLegacyDatesToUnitPickers(data.formFills, mode);
            }
        } catch {
            appendMessage('assistant', 'Failed to reach AI assistant.');
        } finally {
            btn.removeAttribute('disabled');
        }
    }

    function appendMessage(role: 'user' | 'assistant', text: string): HTMLElement {
        const isUser = role === 'user';
        const div = document.createElement('div');
        div.className = `d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'} mb-2`;
        const bubble = document.createElement('div');
        bubble.className = `p-2 rounded small ${isUser ? 'bg-primary text-white' : 'bg-light text-dark'}`;
        bubble.style.maxWidth = '85%';
        bubble.style.whiteSpace = 'pre-wrap';
        bubble.textContent = text;
        div.appendChild(bubble);
        msgsEl.appendChild(div);
        msgsEl.scrollTop = msgsEl.scrollHeight;
        return bubble;
    }
}

// ── Date clamping ──────────────────────────────────────────────────────────

function clamp(v: string, min: string, max: string): string {
    if (!v) return v;
    if (min && v < min) return min;
    if (max && v > max) return max;
    return v;
}

// ── Unit date-range fills ──────────────────────────────────────────────────

function applyUnitDateRanges(ranges: UnitDateRange[], mode: AssistantMode) {
    if (mode === 'Verify') return;

    for (const range of ranges) {
        tryApplyUnitDateRange(range, mode);
    }
}

function tryApplyUnitDateRange(range: UnitDateRange, mode: AssistantMode) {
    const containers = [
        document.getElementById('accommodationPricesContent'),
        document.getElementById('insurancePricesContent'),
    ].filter((el): el is HTMLElement => el !== null);

    for (const container of containers) {
        const card = container.querySelector<HTMLElement>(
            `.cc-unit-selection-card[data-unit-id="${range.unitId}"]`
        );
        if (card) {
            writeUnitPickers(card, range, mode);
            return;
        }
    }

    // Card not rendered yet — watch for it with a MutationObserver.
    for (const container of containers) {
        const obs = new MutationObserver(() => {
            const card = container.querySelector<HTMLElement>(
                `.cc-unit-selection-card[data-unit-id="${range.unitId}"]`
            );
            if (card) {
                obs.disconnect();
                writeUnitPickers(card, range, mode);
            }
        });
        obs.observe(container, { childList: true, subtree: true });

        // Safety: disconnect after 15s to avoid leaking the observer.
        setTimeout(() => obs.disconnect(), 15000);
    }
}

function writeUnitPickers(card: HTMLElement, range: UnitDateRange, mode: AssistantMode) {
    const fromPicker = card.querySelector<HTMLInputElement>('.sub-range-from-picker');
    const toPicker   = card.querySelector<HTMLInputElement>('.sub-range-to-picker');
    if (!fromPicker || !toPicker) return;

    if (mode === 'AutoFill' || !range.isSuggestion) {
        if (range.from) {
            fromPicker.value = clamp(range.from, fromPicker.min, fromPicker.max);
            fromPicker.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (range.to) {
            toPicker.value = clamp(range.to, toPicker.min, toPicker.max);
            toPicker.dispatchEvent(new Event('change', { bubbles: true }));
        }
    } else {
        // Highlight mode: show suggestion pills near the unit card.
        renderUnitDateSuggestion(card, fromPicker, toPicker, range);
    }
}

function renderUnitDateSuggestion(
    card: HTMLElement,
    fromPicker: HTMLInputElement,
    toPicker: HTMLInputElement,
    range: UnitDateRange
) {
    const existing = card.querySelector('.ai-unit-date-suggestion');
    if (existing) existing.remove();

    const pill = document.createElement('div');
    pill.className = 'ai-unit-date-suggestion d-flex align-items-center gap-2 mt-1 p-1 rounded border border-warning-subtle bg-warning-subtle small';
    pill.innerHTML =
        `<span class="text-body-secondary">AI dates:</span>` +
        ` <strong>${escapeHtml(range.from)}</strong>` +
        ` – <strong>${escapeHtml(range.to)}</strong>`;

    const accept = document.createElement('button');
    accept.type = 'button';
    accept.className = 'btn btn-sm btn-success py-0 px-1';
    accept.textContent = '✓';
    accept.addEventListener('click', () => {
        if (range.from) {
            fromPicker.value = clamp(range.from, fromPicker.min, fromPicker.max);
            fromPicker.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (range.to) {
            toPicker.value = clamp(range.to, toPicker.min, toPicker.max);
            toPicker.dispatchEvent(new Event('change', { bubbles: true }));
        }
        pill.remove();
    });

    const reject = document.createElement('button');
    reject.type = 'button';
    reject.className = 'btn btn-sm btn-outline-secondary py-0 px-1';
    reject.textContent = '✗';
    reject.addEventListener('click', () => pill.remove());

    pill.appendChild(accept);
    pill.appendChild(reject);

    // Insert after the date-row inside the card.
    const dateRow = card.querySelector('.cc-unit-date-row');
    if (dateRow) {
        dateRow.insertAdjacentElement('afterend', pill);
    } else {
        card.appendChild(pill);
    }
}

// ── Legacy bridge ─────────────────────────────────────────────────────────
// When the AI proposes CheckInDate/CheckOutDate + AccommodationOfferIds but no unitDateRanges,
// forward those dates to the first unit card of the selected offer.

function bridgeLegacyDatesToUnitPickers(fills: FormFill[], mode: AssistantMode) {
    if (mode === 'Verify') return;

    const checkIn  = fills.find(f => f.field === 'CheckInDate')?.value  ?? '';
    const checkOut = fills.find(f => f.field === 'CheckOutDate')?.value ?? '';
    if (!checkIn && !checkOut) return;

    const accFill = fills.find(f => f.field === 'AccommodationOfferIds');
    const insFill = fills.find(f => f.field === 'InsuranceOfferIds');
    if (!accFill && !insFill) return;

    const isSuggestion = fills.find(f => f.field === 'CheckInDate')?.isSuggestion ?? true;

    const containers: HTMLElement[] = [];
    if (accFill) {
        const el = document.getElementById('accommodationPricesContent');
        if (el) containers.push(el);
    }
    if (insFill) {
        const el = document.getElementById('insurancePricesContent');
        if (el) containers.push(el);
    }

    for (const container of containers) {
        tryWriteFirstUnitPicker(container, checkIn, checkOut, isSuggestion, mode);
    }
}

function tryWriteFirstUnitPicker(
    container: HTMLElement,
    checkIn: string,
    checkOut: string,
    isSuggestion: boolean,
    mode: AssistantMode
) {
    const card = container.querySelector<HTMLElement>('.cc-unit-selection-card');
    if (card) {
        const range: UnitDateRange = {
            unitId: parseInt(card.dataset.unitId ?? '0'),
            from: checkIn,
            to: checkOut,
            isSuggestion,
        };
        writeUnitPickers(card, range, mode);
        return;
    }

    // Not rendered yet — watch for the first card to appear.
    const obs = new MutationObserver(() => {
        const firstCard = container.querySelector<HTMLElement>('.cc-unit-selection-card');
        if (firstCard) {
            obs.disconnect();
            const range: UnitDateRange = {
                unitId: parseInt(firstCard.dataset.unitId ?? '0'),
                from: checkIn,
                to: checkOut,
                isSuggestion,
            };
            writeUnitPickers(firstCard, range, mode);
        }
    });
    obs.observe(container, { childList: true, subtree: true });
    setTimeout(() => obs.disconnect(), 15000);
}

// ── Form fill helpers ──────────────────────────────────────────────────────

function findOption(el: HTMLSelectElement, value: string): HTMLOptionElement | null {
    return Array.from(el.options).find(o => o.value === value) ?? null;
}

function dispatchChange(el: HTMLElement): void {
    el.dispatchEvent(new Event('change', { bubbles: true }));
}

function applyFormFills(fills: FormFill[], mode: AssistantMode) {
    if (mode === 'Verify') return;

    fills.forEach(fill => {
        const el = document.querySelector<HTMLInputElement | HTMLSelectElement>(
            `[name="${fill.field}"], #${fill.field}`
        );
        if (!el) {
            console.warn('[ai-assistant] Unknown field:', fill.field);
            return;
        }

        if (mode === 'AutoFill' || !fill.isSuggestion) {
            applyFill(el, fill);
        } else {
            renderFillSuggestion(el, fill);
        }
    });
}

function applyFill(el: HTMLInputElement | HTMLSelectElement, fill: FormFill) {
    if (el instanceof HTMLSelectElement && el.multiple) {
        // Offer-id multi-select: find the matching option and toggle it on.
        // The underlying <select> is hidden behind a custom listbox — it watches
        // option.selected changes via a MutationObserver on childList.
        const opt = findOption(el, fill.value);
        if (!opt) { console.warn('[ai-assistant] Option not found in', el.id, ':', fill.value); return; }
        opt.selected = true;
        dispatchChange(el);
        return;
    }

    if (el instanceof HTMLSelectElement) {
        const opt = findOption(el, fill.value);
        if (!opt) { console.warn('[ai-assistant] Option not found in', el.id, ':', fill.value); return; }
        el.value = fill.value;
        dispatchChange(el);
        return;
    }

    if (el instanceof HTMLInputElement && DATE_FIELDS.has(fill.field)) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(fill.value)) {
            console.warn('[ai-assistant] Invalid date format for', fill.field, '(expected YYYY-MM-DD):', fill.value);
            return;
        }
    }

    el.value = fill.value;
    dispatchChange(el);
}

function renderFillSuggestion(el: HTMLInputElement | HTMLSelectElement, fill: FormFill) {
    const existing = el.parentElement?.querySelector('.ai-fill-suggestion');
    if (existing) existing.remove();

    const pill = document.createElement('div');
    pill.className = 'ai-fill-suggestion d-flex align-items-center gap-2 mt-1 p-1 rounded border border-warning-subtle bg-warning-subtle small';
    pill.innerHTML = `<span class="text-body-secondary">${escapeHtml(fill.label ?? fill.field)}:</span> <strong>${escapeHtml(fill.value)}</strong>`;

    const accept = document.createElement('button');
    accept.type = 'button';
    accept.className = 'btn btn-sm btn-success py-0 px-1';
    accept.textContent = '✓';
    accept.addEventListener('click', () => { applyFill(el, fill); pill.remove(); });

    const reject = document.createElement('button');
    reject.type = 'button';
    reject.className = 'btn btn-sm btn-outline-secondary py-0 px-1';
    reject.textContent = '✗';
    reject.addEventListener('click', () => pill.remove());

    pill.appendChild(accept);
    pill.appendChild(reject);

    const wrapper = el.closest('.form-group') ?? el.parentElement;
    wrapper?.insertAdjacentElement('afterend', pill);
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
